import {
  DeleteOutline,
  DeleteOutlined,
  EditOutlined,
} from '@mui/icons-material';
import {
  Checkbox,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-premium';
import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import {
  useItemsBulkUpdateMutation,
  useLazyPostGroupsQuery,
  useLazyPostMenusQuery,
  usePostItemsQuery,
} from 'api/ordering-service/ordering-service';
import { ItemResult } from 'api/ordering-service/ordering-service.types';
import { additionalPropertiesToObject } from 'api/ordering-service/ordering-service.utils';
import { UtilityDrawerHookReturnType } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import { ToolbarProps } from 'components/inc-data-grid/inc-data-grid.component';
import { useSearch } from 'components/search/search.hooks';
import {
  defaultSearchParams,
  querySearchParam,
  stringListSearchParam,
  stringSearchParam,
} from 'components/search/search.utils';
import { useHasPermission } from 'hooks/authorization.hooks';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from 'util/currency';
import InventoryChip from './items-data-grid/inventory-chip/inventory-chip.component';
import LocationsInfo from './items-data-grid/locations-info/locations-info.component';
import { useBulkEditDataGridContext } from 'components/inc-data-grid/bulk-edit-data-grid/bulk-edit-data-grid.context';
import {
  displayInfoColumnVisibilityModel,
  useMenuManagementBulkEditFields,
  useMenuManagementDisplayInfoColumns,
} from 'pages/menu-management/menu-management.hooks';
import {
  toBulkUpdateField,
  toBulkUpdateValue,
} from 'pages/menu-management/menu-management-data-grid.utils';
import IncActiveInactive from 'components/inc-data-grid/cells/inc-active-inactive-status-chip/inc-active-inactive-status-chip.component';
import { BulkEditGridColDef } from 'components/inc-data-grid/bulk-edit-data-grid.types';

export const useItemsFilter = (isImported = false) => {
  const { t } = useTranslation();
  const [triggerMenus] = useLazyPostMenusQuery();
  const [triggerGroups] = useLazyPostGroupsQuery();

  const statusOptions = useMemo(
    () => [
      {
        label: t('ItemsDataGrid.filter.status.allOption'),
        id: 'ALL',
      },
      {
        label: t('ItemsDataGrid.filter.status.active'),
        id: 'ACTIVE',
      },
      {
        label: t('ItemsDataGrid.filter.status.inactive'),
        id: 'INACTIVE',
      },
    ],
    [t],
  );

  const inventoryOptions = useMemo(
    () => [
      {
        label: t('ItemsDataGrid.filter.inventory.allOption'),
        id: 'ALL',
      },
      {
        label: t('ItemsDataGrid.filter.inventory.outOfStock'),
        id: 'OUT_OF_STOCK',
      },
      {
        label: t('ItemsDataGrid.filter.inventory.outOfStockToday'),
        id: 'OUT_OF_STOCK_TODAY',
      },
      {
        label: t('ItemsDataGrid.filter.inventory.inStock'),
        id: 'IN_STOCK',
      },
    ],
    [t],
  );

  const searchParams = useMemo(
    () => [
      ...defaultSearchParams({
        count: 10,
        page: 0,
        sortBy: 'title',
        sortDirection: 'ASC',
      }),
      querySearchParam('title', {
        category: t('ItemsDataGrid.filter.title'),
      }),
      querySearchParam('originalTitle', {
        category: t('ItemsDataGrid.filter.externalTitle'),
      }),
      querySearchParam('incentivioIds', {
        category: t('ItemsDataGrid.filter.id'),
      }),
      querySearchParam('externalIds', {
        category: t('ItemsDataGrid.filter.externalId'),
      }),
      stringSearchParam('includeStores', {
        defaultValue: 'true',
      }),
      stringSearchParam('imported', {
        defaultValue: isImported ? 'true' : 'false',
      }),
      stringListSearchParam('catalogIds', {
        defaultValue: ['ALL'],
        options: [
          {
            label: t('ItemsDataGrid.filter.menu.allOption'),
            id: 'ALL',
          },
        ],
        asyncOptions: async () => {
          const catalogs = await triggerMenus({
            count: 300,
            page: 0,
            storeIds: '*',
            imported: isImported ? 'true' : 'false',
          });
          const dataArray = catalogs?.data?.catalogs.map(catalog => ({
            label: catalog.title,
            id: catalog.catalogId,
          }));

          return [
            {
              label: t('ItemsDataGrid.filter.menu.allOption'),
              id: 'ALL',
            },
          ].concat(dataArray ?? []);
        },
        filter: {
          type: 'MultiSelectFilter',
          label: t('ItemsDataGrid.filter.menu.label'),
        },
      }),
      stringListSearchParam('groupIds', {
        defaultValue: ['ALL'],
        options: [
          {
            label: t('ItemsDataGrid.filter.group.allOption'),
            id: 'ALL',
          },
        ],
        asyncOptions: async () => {
          const groups = await triggerGroups({
            count: 300,
            page: 0,
            imported: isImported ? 'true' : 'false',
          });
          const dataArray = groups?.data?.groups.map(group => ({
            label: group.title,
            id: group.groupId,
          }));

          return [
            {
              label: t('ItemsDataGrid.filter.group.allOption'),
              id: 'ALL',
            },
          ].concat(dataArray ?? []);
        },
        filter: {
          type: 'MultiSelectFilter',
          label: t('ItemsDataGrid.filter.group.label'),
        },
      }),
      stringSearchParam('status', {
        defaultValue: 'ALL',
        options: statusOptions,
        filter: {
          type: 'SingleSelectFilter',
          label: t('ItemsDataGrid.filter.status.label'),
        },
        toQueryString: value => (value === 'ALL' ? '' : value),
      }),
      stringSearchParam('inventory', {
        defaultValue: 'ALL',
        options: inventoryOptions,
        filter: {
          type: 'SingleSelectFilter',
          label: t('ItemsDataGrid.filter.inventory.label'),
        },
        toQueryString: value => (value === 'ALL' ? '' : value),
      }),
    ],
    [
      inventoryOptions,
      isImported,
      statusOptions,
      t,
      triggerGroups,
      triggerMenus,
    ],
  );

  const modifiedSearchParams = useMemo(() => {
    if (!isImported) {
      return searchParams.filter(
        ({ paramName }) =>
          paramName !== 'originalTitle' && paramName !== 'externalIds',
      );
    }
    return searchParams;
  }, [isImported, searchParams]);

  return useSearch(usePostItemsQuery, modifiedSearchParams);
};

export const useItemsDataGrid = (
  isImported: boolean,
  deleteConfirmationDrawer: UtilityDrawerHookReturnType<ItemResult>,
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const hasEditItemPermission = useHasPermission('EDIT_ITEM');
  const hasDeleteItemPermission = useHasPermission('DELETE_ITEM');
  const displayInfoColumns = useMenuManagementDisplayInfoColumns();

  const getActions = useCallback(
    params => {
      const actions = [];

      if (hasEditItemPermission) {
        actions.push(
          <GridActionsCellItem
            icon={<EditOutlined />}
            label={t('ItemsDataGrid.actions.edit.label')}
            onClick={() => navigate(`../${params.id}`)}
            showInMenu
          />,
        );
      }

      if (hasDeleteItemPermission) {
        actions.push(
          <GridActionsCellItem
            icon={<DeleteOutline color="error" />}
            label={t('ItemsDataGrid.actions.delete.label')}
            onClick={() => {
              deleteConfirmationDrawer.setDrawerData(params.row);
              deleteConfirmationDrawer.setOpen(true);
            }}
            showInMenu
          />,
        );
      }

      return actions;
    },
    [
      deleteConfirmationDrawer,
      hasDeleteItemPermission,
      hasEditItemPermission,
      navigate,
      t,
    ],
  );

  const columns: BulkEditGridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: t('ItemsDataGrid.column.title.label'),
        minWidth: 500,
        hideable: false,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'originalTitle',
        headerName: t('ItemsDataGrid.column.externalTitle.label'),
        minWidth: 240,
        hideable: true,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'storeInfos',
        headerName: t('ItemsDataGrid.column.locations.label'),
        minWidth: 200,
        hideable: true,
        sortable: false,
        display: 'flex',
        renderCell: params => <LocationsInfo stores={params.value} />,
      },
      {
        field: 'price',
        headerName: t('ItemsDataGrid.column.price.label'),
        minWidth: 80,
        hideable: true,
        type: 'number',
        display: 'flex',
        renderCell: params => (
          <Typography>{formatCurrency(params.value ?? 0)}</Typography>
        ),
      },
      {
        field: 'itemType',
        headerName: t('ItemsDataGrid.column.type.label'),
        minWidth: 80,
        hideable: true,
        type: 'singleSelect',
        valueOptions: [
          { id: 'MAIN', label: 'Main' },
          { id: 'MODIFIER', label: 'Modifier' },
        ],
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'itemId',
        headerName: t('ItemsDataGrid.column.id.label'),
        minWidth: 200,
        hideable: true,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'externalId',
        headerName: t('ItemsDataGrid.column.externalId.label'),
        minWidth: 280,
        hideable: true,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'inheritParentOptions',
        headerName: t('ItemsDataGrid.column.inheritParent.label'),
        minWidth: 180,
        hideable: true,
        display: 'flex',
        renderCell: params => (
          <Checkbox defaultChecked={params.value} disabled />
        ),
      },
      {
        field: 'taxable',
        headerName: t('ItemsDataGrid.column.taxable.label'),
        minWidth: 80,
        hideable: true,
        display: 'flex',
        renderCell: params => (
          <Checkbox defaultChecked={params.value} disabled />
        ),
      },
      {
        field: 'invertParentTaxable',
        headerName: t('ItemsDataGrid.column.reverseTax.label'),
        minWidth: 150,
        hideable: true,
        display: 'flex',
        renderCell: params => (
          <Checkbox defaultChecked={params.value} disabled />
        ),
      },
      {
        field: 'inventory',
        headerName: t('ItemsDataGrid.column.inventory.label'),
        minWidth: 180,
        hideable: true,
        type: 'singleSelect',
        valueOptions: [
          { id: 'OUT_OF_STOCK', label: 'Out of Stock' },
          { id: 'OUT_OF_STOCK_TODAY', label: 'Out of Stock Today' },
          { id: 'IN_STOCK', label: 'In Stock' },
        ],
        display: 'flex',
        renderCell: params => (
          <InventoryChip
            outOfStock={params.row.outOfStock}
            outOfStockToday={params.row.outOfStockToday}
          />
        ),
      },
      {
        field: 'extendedAttributes',
        headerName: t('ItemsDataGrid.column.extendedAttributes.label'),
        minWidth: 300,
        hideable: true,
        sortable: false,
        type: 'custom',
        customType: 'keyValue',
        display: 'flex',
        renderCell: params => (
          <Typography title={JSON.stringify(params.value)}>
            {Object.keys(params.value).length > 0 &&
              JSON.stringify(params.value)}
          </Typography>
        ),
      },
      {
        field: 'startDate',
        headerName: t('ItemsDataGrid.column.startDate.label'),
        minWidth: 200,
        hideable: true,
        display: 'flex',
        renderCell: params => (
          <Typography>
            {DateTime.fromISO(params.value).toLocaleString(
              DateTime.DATETIME_MED,
            )}
          </Typography>
        ),
      },
      {
        field: 'endDate',
        headerName: t('ItemsDataGrid.column.endDate.label'),
        minWidth: 200,
        hideable: true,
        display: 'flex',
        renderCell: params => (
          <Typography>
            {DateTime.fromISO(params.value).toLocaleString(
              DateTime.DATETIME_MED,
            )}
          </Typography>
        ),
      },
      {
        field: 'displayRank',
        headerName: t('ItemsDataGrid.column.displayRank.label'),
        hideable: true,
        minWidth: 150,
        type: 'number',
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'status',
        headerName: t('ItemsDataGrid.column.status.label'),
        hideable: false,
        minWidth: 100,
        flex: 1,
        type: 'singleSelect',
        valueOptions: [
          { id: 'Active', label: 'Active' },
          { id: 'Inactive', label: 'Inactive' },
        ],
        display: 'flex',
        renderCell: params => <IncActiveInactive value={params.value} />,
      },
      ...displayInfoColumns,
      ...(!hasEditItemPermission && !hasDeleteItemPermission
        ? []
        : [
            {
              field: t('SearchDataGrid.column.actions'),
              type: 'actions',
              hideable: false,
              getActions,
              resizable: false,
            } as GridColDef,
          ]),
    ],
    [
      displayInfoColumns,
      getActions,
      hasDeleteItemPermission,
      hasEditItemPermission,
      t,
    ],
  );

  return !isImported
    ? columns.filter(
        column =>
          column.field !== 'originalTitle' && column.field !== 'externalId',
      )
    : columns;
};

export const useItemsToolbar = (
  bulkDeleteConfirmationDrawer: UtilityDrawerHookReturnType<ItemResult>,
  isImported: boolean,
): {
  toolbar: ToolbarProps;
  initialState: GridInitialStatePremium;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [triggerBulkEdit] = useItemsBulkUpdateMutation();
  const { selectedRowIds } = useBulkEditDataGridContext();

  const hasEditItemPermission = useHasPermission('EDIT_ITEM');
  const hasDeleteItemPermission = useHasPermission('DELETE_ITEM');

  const snackbarForBulkEdit = field => {
    switch (field) {
      case 'status':
        return t('ItemsDataGrid.bulkEdit.status.successMessage');
      case 'displayRank':
        return t('ItemsDataGrid.bulkEdit.displayRank.successMessage');
      case 'extendedAttributes':
        return t('ItemsDataGrid.bulkEdit.extendedAttributes.successMessage');
      case 'startDate':
        return t('ItemsDataGrid.bulkEdit.startDate.successMessage');
      case 'endDate':
        return t('ItemsDataGrid.bulkEdit.endDate.successMessage');
      case 'price':
        return t('ItemsDataGrid.bulkEdit.price.successMessage');
      case 'itemType':
        return t('ItemsDataGrid.bulkEdit.type.successMessage');
      case 'inventory':
        return t('ItemsDataGrid.bulkEdit.inventory.successMessage');
      case 'displayTitle':
        return t('ItemsDataGrid.bulkEdit.displayTitle.successMessage');
      case 'shortDescription':
        return t('ItemsDataGrid.bulkEdit.shortDescription.successMessage');
      case 'longDescription':
        return t('ItemsDataGrid.bulkEdit.longDescription.successMessage');
      case 'smallImage':
        return t('ItemsDataGrid.bulkEdit.smallImage.successMessage');
      case 'mediumImage':
        return t('ItemsDataGrid.bulkEdit.mediumImage.successMessage');
    }
  };

  const modifiedValue = (field: string, value: any) => {
    switch (field) {
      case 'extendedAttributes':
        return additionalPropertiesToObject(value);
      case 'startDate':
        return DateTime.fromISO(value)
          .set({ hour: 0, minute: 0, second: 0 })
          .toISO({ includeOffset: false, suppressMilliseconds: true });
      case 'endDate':
        return DateTime.fromISO(value)
          .set({ hour: 23, minute: 59, second: 59 })
          .toISO({ includeOffset: false, suppressMilliseconds: true });
      case 'price':
        return value * 1000;
      default:
        return toBulkUpdateValue(field, value);
    }
  };

  const modifiedField = (field: string) => {
    switch (field) {
      case 'itemType':
        return 'type';
      default:
        return toBulkUpdateField(field);
    }
  };

  const {
    status,
    startDate,
    endDate,
    extendedAttributes,
    displayRank,
    price,
    itemType,
    inventory,
    displayInfo,
  } = useMenuManagementBulkEditFields({ smallImageObjectType: 'ITEM' });

  const bulkEditFields = useMemo(() => {
    if (hasEditItemPermission) {
      return {
        status,
        startDate,
        endDate,
        extendedAttributes,
        displayRank,
        // price, type and inventory come from the POS for imported items
        ...(isImported ? {} : { price, itemType, inventory }),
        ...displayInfo,
      };
    }
    return {};
  }, [
    displayInfo,
    displayRank,
    endDate,
    extendedAttributes,
    hasEditItemPermission,
    inventory,
    isImported,
    itemType,
    price,
    startDate,
    status,
  ]);

  const bulkEditAdditionalMenus = useMemo(
    () =>
      hasDeleteItemPermission
        ? [
            <MenuItem
              key="delete"
              onClick={() => bulkDeleteConfirmationDrawer.setOpen(true)}
            >
              <ListItemIcon>
                <DeleteOutlined fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>
                {t('ItemsDataGrid.actions.delete.label')}
              </ListItemText>
            </MenuItem>,
          ]
        : undefined,
    [bulkDeleteConfirmationDrawer, hasDeleteItemPermission, t],
  );

  const handleBulkUpdate = async data => {
    const field = Object.keys(data)[0];
    const value = data[field];

    try {
      await triggerBulkEdit({
        ids: selectedRowIds,
        field: modifiedField(field),
        value: modifiedValue(field, value),
      }).unwrap();
      enqueueSnackbar(snackbarForBulkEdit(field), {
        severity: 'success',
      });
    } catch (_err) {}
  };

  const toolbar: ToolbarProps = {
    columns: { enabled: true },
    bulkEdit: {
      enabled: hasEditItemPermission || hasDeleteItemPermission,
      onValid: data => handleBulkUpdate(data),
      fields: bulkEditFields,
      additionalMenuItems: bulkEditAdditionalMenus,
    },
  };

  const initialState: GridInitialStatePremium = {
    pinnedColumns: { right: [t('SearchDataGrid.column.actions')] },
    columns: {
      columnVisibilityModel: {
        itemId: false,
        externalId: false,
        inheritParentOptions: false,
        taxable: false,
        invertParentTaxable: false,
        inventory: false,
        extendedAttributes: false,
        startDate: false,
        endDate: false,
        displayRank: false,
        ...displayInfoColumnVisibilityModel,
      },
    },
  };

  return { toolbar, initialState };
};
